import _ from 'lodash';
import React, { FC, useState, useRef, HTMLAttributes, useEffect, useMemo } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Edge,
  Node,
  Handle,
  Position,
  Controls,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme from 'design-system/theme';
import dagre from 'dagre';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { Badge } from 'design-system';
import { makeStyles } from 'design-system/styles';

import { PipelineDefinitionNode } from '@scale/shared/pipelinev3/lookup';
import { NodeData } from '@scale/shared/pipelinev3/types';

const NODE_WIDTH = 260;
const NODE_HEIGHT = 160;

const useStyles = makeStyles({
  badgeRoot: {
    width: '100%',
  },
  badge: {
    padding: 0,
  },
  badgeHidden: {
    transform: 'scale(0) translate(50%, -50%) !important',
  },
  nodeDetailsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  nodeDetailsLeftColumn: {
    flexBasis: '30%',
    color: '#CCCCCC',
  },
  nodeDetailsRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '70%',
  },
  nodeDetails: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    textAlign: 'left',
    fontFamily: theme.typography.fontFamily,
    pointerEvents: 'none',
    background: 'rgb(51, 51, 51)',
    color: 'rgb(255, 255, 255)',
    width: NODE_WIDTH,
    minHeight: NODE_HEIGHT,
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 8px',
    borderRadius: '12px',
    padding: '0px 0px 12px',

    '& a': {
      pointerEvents: 'auto !important',
    },
  },
  nodeDetailsHeader: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: '12px',
    padding: '8px',
    borderRadius: '12px 12px 0 0',
  },
  nodeDetailsContent: {
    padding: '0 12px',
  },
  nodeDetailsTitle: {
    flexBasis: '70%',
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 600,
  },
});

interface IGeneralPipelineNodeGraphProps extends HTMLAttributes<HTMLDivElement> {
  nodes: PipelineDefinitionNode[];
  height?: string;
  nodesSelectable?: boolean;
}

const EDGE_COLOR_MAP: Record<string, string> = {
  active: '#F39212',
  complete: '#29CD9C',
  execution_error: '#cc0000',
  invalid_execution: '#cc0000',
  resource: '#0e4d92',
};

const renderMetadataRow = (key: string, value: JSX.Element | string | object, classes: any) => {
  // FIXME this is not the right way to do this
  const valueToRender =
    React.isValidElement(value) || _.isString(value) ? value : JSON.stringify(value);
  return (
    <div key={key} className={classes.nodeDetailsRow}>
      <div className={classes.nodeDetailsLeftColumn}>{key}</div>
      <div className={classes.nodeDetailsRightColumn}>{valueToRender}</div>
      <style jsx>{``}</style>
    </div>
  );
};

const PipelineNodeNode: FC<any> = ({ id, data: node, selected }) => {
  const classes = useStyles();
  const { isRemoAdmin } = useRemoAdminCtx();

  const url = `${isRemoAdmin ? '/remoadmin/tools' : '/corp'}/lookup/${id}`;

  return (
    <div className={classes.nodeDetails} style={{ opacity: '1' }}>
      <Handle type="target" position={Position.Left} style={{ background: '#fff' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#fff' }} />
      <Badge
        classes={{
          root: classes.badgeRoot,
          badge: classes.badge,
          invisible: selected ? undefined : classes.badgeHidden,
        }}
        invisible={!selected}
        badgeContent={<FontAwesomeIcon icon="check" />}
        showZero={false}
        color="primary"
      >
        <div
          className={classes.nodeDetailsHeader}
          style={{ background: 'rgba(41, 205, 156, 0.4)' }}
        >
          <div className={classes.nodeDetailsTitle}>Node: {_.startCase(node.name)}</div>
        </div>
      </Badge>
      <div className={classes.nodeDetailsContent}>
        {renderMetadataRow('ID', <a href={url}>{id}</a>, classes)}
        {renderMetadataRow('Count', node.count, classes)}
        {node.substageDefinitions &&
          renderMetadataRow('Substages', node.substageDefinitions.length, classes)}
      </div>
    </div>
  );
};

const generateDagre = (nodes: any[], edges: any[]) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 100, ranksep: 100 });
  g.setDefaultEdgeLabel(function () {
    return {};
  });

  const nodeMap: Record<string, any> = {};
  for (const n of nodes) {
    g.setNode(n.id, { width: n.width, height: n.height });
    nodeMap[n.id] = n;
  }
  const edgesMap = new Map();
  for (const e of edges) {
    edgesMap.set(`${e.start}-${e.end}`, e);
    g.setEdge(e.start, e.end);
  }

  dagre.layout(g);

  const retNodes: Node[] = [];
  g.nodes().forEach(nid => {
    const { x, y } = g.node(nid);
    const { dagreType, ...data } = nodeMap[nid];
    retNodes.push({
      id: nid,
      type: dagreType,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      data,
    } as unknown as Node);
  });

  const retEdges: Edge[] = [];
  g.edges().forEach(e => {
    const edgeId = `${e.v}-${e.w}`;
    const edgeColor =
      e.v.startsWith('r-') || e.w.startsWith('r-')
        ? EDGE_COLOR_MAP.resource
        : EDGE_COLOR_MAP[nodeMap[e.v].status];
    const edgeOpacity = '1';
    return retEdges.push({
      id: edgeId,
      source: e.v,
      target: e.w,
      arrowHeadType: 'arrow' as any,
      style: { stroke: edgeColor, strokeDasharray: 10, opacity: edgeOpacity },
      label: edgesMap.get(edgeId)?.label,
    } as unknown as Edge);
  });

  return { nodes: retNodes, edges: retEdges };
};

const getNodesAndEdges = (pipelineNodes: PipelineDefinitionNode[]) => {
  const nodeNodes = pipelineNodes.map(n => ({
    ...n,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    dagreType: 'pipelineNode',
  }));

  // const validIds = nodes.map(n => n.id);
  const edges: { start: string; end: string; label: string }[] = [];
  // for (const pn of pipelineNodes) {
  //   for (const depId of pn.dependencyIds) {
  //     if (validIds.includes(depId)) {
  //       edges.push({ start: depId, end: pn.id, label: _.get(pn, 'input.transitionReason') });
  //     }
  //   }
  //   for (const depId of pn.inputResourceIds) {
  //     if (validIds.includes(depId)) {
  //       edges.push({ start: depId, end: pn.id });
  //     }
  //   }
  //   for (const depId of pn.outputResourceIds) {
  //     if (validIds.includes(depId)) {
  //       edges.push({ start: pn.id, end: depId });
  //     }
  //   }
  // }

  return { nodes: nodeNodes, edges };
};

const GeneralPipelineNodeGraph: FC<IGeneralPipelineNodeGraphProps> = ({
  nodes,
  height = '575px',
  className,
  nodesSelectable = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInteractive, setIsInteractive] = useState(false);
  const handleBlur = () => setIsInteractive(false);
  const handleFocus = () => setIsInteractive(true);

  const [selectedNodeDataArray, setSelectedNodeDataArray] = useState<NodeData[]>([]);

  const recomputeGraph = () => {
    const { nodes: graphNodes, edges: graphEdges } = getNodesAndEdges(nodes);
    return generateDagre(graphNodes, graphEdges);
  };

  const { nodes: initialNodes, edges: initialEdges } = useMemo(recomputeGraph, []);
  const [dagreNodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [dagreEdges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    const graph = recomputeGraph();
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }, []);

  const onSelectionChange = (elements: { nodes: Node[]; edges: Edge[] }) => {
    if (!elements) {
      return;
    }
    const nodeIds: string[] = [];
    elements.nodes.map(elem => (elem.id[0] === 'n' ? nodeIds.push(elem.id) : null));

    const nodeDataArray: NodeData[] = [];
    elements.nodes.map(elem =>
      elem.id[0] === 'n'
        ? nodeDataArray.push({
            id: elem.data.id,
            name: elem.data.nodeName,
            taskId: elem.data.taskId,
            status: elem.data.status,
          })
        : null,
    );
    const updatedNodeDataArray = _.compact(nodeDataArray);
    if (!_.isEqual(selectedNodeDataArray, updatedNodeDataArray)) {
      setSelectedNodeDataArray(updatedNodeDataArray);
    }
  };

  const nodeTypes = useMemo(() => ({ pipelineNode: PipelineNodeNode }), []);

  return (
    <div
      className={className}
      ref={containerRef}
      style={{
        cursor: isInteractive ? 'grab' : 'initial',
        boxShadow: isInteractive ? 'inset 0px 0px 6px #ccc' : 'none',
        height,
        display: 'flex',
        flexDirection: 'column',
      }}
      onBlur={handleBlur}
      onFocus={handleFocus}
      tabIndex={1}
    >
      <ReactFlowProvider>
        <ReactFlow
          minZoom={0.25}
          maxZoom={1.25}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          panOnDrag={isInteractive}
          nodes={dagreNodes}
          edges={dagreEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onSelectionChange={onSelectionChange}
          style={{ width: '100%' }}
          fitView={true}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
      <style jsx global>{`
        .react-flow__node {
          cursor: ${nodesSelectable ? 'pointer' : 'initial'};
          border: none;
        }

        .react-flow__edges {
          pointer-events: none !important;
        }
        .react-flow__edge-path {
          stroke-width: 1.5;
        }

        .react-flow__handle {
          visibility: hidden;
        }
        .react-flow__handle-right {
          right: 0;
        }
        .react-flow__handle-left {
          left: 0;
        }
        .react-flow__handle-top {
          top: 0;
        }
        .react-flow__handle-bottom {
          bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default GeneralPipelineNodeGraph;
